import { Youtube, ArrowRight, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import YouTubeEmbed from "@/components/YouTubeEmbed";

const CHANNEL_URL = "https://www.youtube.com/@eclipsegrupo";

// Sección reutilizable de "X en acción" — 1 video featured + texto descriptivo + CTAs
export default function VideoSection({
    eyebrow,
    title,
    subtitle,
    videoId,
    videoTitle,
    bullets = [],
    primaryCtaLabel,
    onPrimaryCta,
    secondaryCtaLabel = "Ver canal completo",
    sectionId,
    testId = "video-section",
    accent = "default",
    layout = "side", // "side" (video 66% + text 33%) | "stacked" (centered) | "short" (9:16 narrow + text)
    isShort = false,
}) {
    const resolvedLayout = isShort ? "short" : layout;
    const isStacked = resolvedLayout === "stacked";
    const isShortLayout = resolvedLayout === "short";
    const accentClasses = accent === "default" ? "text-eclipse-cta" : accent;

    return (
        <section
            id={sectionId}
            data-testid={testId}
            className="bg-white py-16 sm:py-20 lg:py-24 border-y border-neutral-100"
        >
            <div className="container-eclipse">
                <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-12">
                    {eyebrow && (
                        <p className={`font-display font-bold text-[11px] tracking-[0.3em] uppercase mb-3 ${accentClasses}`}>
                            {eyebrow}
                        </p>
                    )}
                    <h2 className="font-display font-black text-eclipse-primary text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mb-3">
                        {title}
                    </h2>
                    {subtitle && (
                        <p className="text-eclipse-muted text-base sm:text-lg">{subtitle}</p>
                    )}
                </div>

                {isStacked ? (
                    <div className="max-w-4xl mx-auto">
                        <YouTubeEmbed videoId={videoId} title={videoTitle} />
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
                            {primaryCtaLabel && (
                                <Button
                                    data-testid={`${testId}-primary-cta`}
                                    onClick={onPrimaryCta}
                                    className="btn-eclipse-cta px-7 py-6 font-display font-bold text-sm tracking-wide"
                                >
                                    {primaryCtaLabel}
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            )}
                            <a
                                href={CHANNEL_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`${testId}-channel-link`}
                                className="inline-flex items-center gap-2 px-6 py-3 font-display font-bold text-sm tracking-wide border-2 border-eclipse-primary text-eclipse-primary hover:bg-eclipse-primary hover:text-white transition-colors rounded-md"
                            >
                                <Youtube className="w-4 h-4" />
                                {secondaryCtaLabel}
                            </a>
                        </div>
                    </div>
                ) : isShortLayout ? (
                    <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8 lg:gap-12 items-center max-w-5xl mx-auto">
                        <div className="w-full max-w-[280px] sm:max-w-xs mx-auto lg:mx-0">
                            <YouTubeEmbed videoId={videoId} title={videoTitle} aspect="short" />
                        </div>
                        <div className="space-y-5">
                            {bullets.length > 0 && (
                                <ul className="space-y-3">
                                    {bullets.map((b, i) => (
                                        <li key={i} className="flex items-start gap-3 text-eclipse-text">
                                            <PlayCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${accentClasses}`} />
                                            <span className="text-sm sm:text-base leading-relaxed">{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <div className="flex flex-col sm:flex-row gap-3 pt-2">
                                {primaryCtaLabel && (
                                    <Button
                                        data-testid={`${testId}-primary-cta`}
                                        onClick={onPrimaryCta}
                                        className="btn-eclipse-cta px-6 py-6 font-display font-bold text-sm tracking-wide"
                                    >
                                        {primaryCtaLabel}
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                )}
                                <a
                                    href={CHANNEL_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`${testId}-channel-link`}
                                    className="inline-flex items-center justify-center gap-2 px-5 py-3 font-display font-bold text-sm tracking-wide border-2 border-eclipse-primary text-eclipse-primary hover:bg-eclipse-primary hover:text-white transition-colors rounded-md"
                                >
                                    <Youtube className="w-4 h-4" />
                                    {secondaryCtaLabel}
                                </a>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="grid lg:grid-cols-3 gap-8 lg:gap-10 items-center">
                        <div className="lg:col-span-2 order-1">
                            <YouTubeEmbed videoId={videoId} title={videoTitle} />
                        </div>
                        <div className="lg:col-span-1 order-2 space-y-5">
                            {bullets.length > 0 && (
                                <ul className="space-y-3">
                                    {bullets.map((b, i) => (
                                        <li key={i} className="flex items-start gap-3 text-eclipse-text">
                                            <PlayCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${accentClasses}`} />
                                            <span className="text-sm sm:text-base leading-relaxed">{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <div className="flex flex-col gap-3 pt-2">
                                {primaryCtaLabel && (
                                    <Button
                                        data-testid={`${testId}-primary-cta`}
                                        onClick={onPrimaryCta}
                                        className="btn-eclipse-cta w-full px-6 py-6 font-display font-bold text-sm tracking-wide"
                                    >
                                        {primaryCtaLabel}
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                )}
                                <a
                                    href={CHANNEL_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`${testId}-channel-link`}
                                    className="inline-flex items-center justify-center gap-2 w-full px-6 py-3 font-display font-bold text-sm tracking-wide border-2 border-eclipse-primary text-eclipse-primary hover:bg-eclipse-primary hover:text-white transition-colors rounded-md"
                                >
                                    <Youtube className="w-4 h-4" />
                                    {secondaryCtaLabel}
                                </a>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}
